import { useEffect, useState } from "react";
import { cn, str } from "./ui";
import { usePage } from "./context";
import { accessLevels, accessRestrictions, type WorkspaceUser } from "./roles";
import type { Role } from "./auth";
import { t } from "@/lib/i18n";

/* Модальное окно «Добавить пользователя»: почта Google, уровень доступа и дополнительные права.
   Пока email не похож на адрес, кнопка «Добавить» неактивна. Список ролей — те, что текущий пользователь может назначать. */

type Props = {
  mode?: "add" | "edit";
  user?: WorkspaceUser;
  onClose: () => void;
  roles: Role[];
  busy?: boolean;
  onSave: (email: string, role: Role, permissions: string[]) => void;
};

const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "•";

function UserModal({ mode = "add", user, roles, busy = false, onClose, onSave }: Props) {
  const { brand } = usePage();
  const [email, setEmail] = useState(user?.email ?? "");
  const levels = accessLevels.filter((l) => roles.includes(l.id));
  const [access, setAccess] = useState<Role>(user && roles.includes(user.role) ? user.role : roles.includes("agent") ? "agent" : (roles[0] ?? "agent"));
  const [advanced, setAdvanced] = useState(Boolean(user?.permissions.length));
  const [restrictions, setRestrictions] = useState<string[]>(user?.permissions ?? []);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const level = accessLevels.find((l) => l.id === access) ?? accessLevels[0];
  const valid = mode === "edit" || EMAIL_OK.test(email.trim());

  const toggle = (id: string) =>
    setRestrictions((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = () => {
    setTouched(true);
    if (!valid || busy) return;
    onSave(email.trim().toLowerCase(), access, restrictions);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-8 backdrop-blur-sm md:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-modal-title"
        className="flash-card relative w-full max-w-lg overflow-hidden rounded-card border border-border bg-card !p-0 shadow-lift"
      >
        <div className="flex items-start gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0 flex-1">
            <h3 id="user-modal-title" className="text-[15px] font-semibold">
              {mode === "edit" ? t("Изменить пользователя") : t("Добавить пользователя")}
            </h3>
            {mode === "edit" && user ? (
              <p className="mt-1 flex items-center gap-2 text-[12px] text-muted-foreground">
                <span className="grid size-6 place-items-center rounded-full bg-primary/10 text-[9px] font-bold text-primary" aria-hidden>
                  {initials(user.name)}
                </span>
                {user.name}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Закрыть")}
            className="grid size-7 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <i className="ri-close-line text-base leading-none" aria-hidden />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-5 py-4">
          <p className="text-[12px] leading-relaxed text-muted-foreground">
            {mode === "edit"
              ? t("Измените уровень доступа пользователя в рабочем пространстве {brand}.", { brand: str(brand) || "Hub" })
              : t("Укажите почту Google сотрудника и уровень доступа. После «Добавить» он сразу сможет войти в {brand} кнопкой «Войти через Google» — письмо не нужно.", { brand: str(brand) || "Hub" })}
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex min-w-0 flex-col gap-1.5">
              <span className="text-[12px] font-medium text-muted-foreground">{t("Почта Google")}</span>
              <input
                autoFocus={mode === "add"}
                type="email"
                value={email}
                readOnly={mode === "edit"}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.ru"
                aria-invalid={touched && !valid}
                className={cn(
                  "h-10 w-full min-w-0 rounded-btn border bg-background px-3 text-[12.5px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/25",
                  touched && !valid ? "border-red-500/70" : "border-border focus:border-primary",
                  mode === "edit" && "cursor-not-allowed opacity-70",
                )}
              />
            </label>

            <label className="flex min-w-0 flex-col gap-1.5">
              <span className="text-[12px] font-medium text-muted-foreground">{t("Уровень доступа")}</span>
              <select
                value={access}
                onChange={(e) => setAccess(e.target.value as Role)}
                className="h-10 w-full min-w-0 cursor-pointer rounded-btn border border-border bg-background px-2.5 text-[12.5px] text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
              >
                {levels.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {touched && !valid ? (
            <span className="-mt-1 text-[11px] text-red-600">{t("Без корректной почты пользователя не добавить")}</span>
          ) : null}

          <p className="rounded-btn bg-foreground/[.04] px-3 py-2.5 text-[11.5px] leading-relaxed text-muted-foreground">
            {level.hint}
          </p>

          <div className="rounded-btn border border-border">
            <button
              type="button"
              onClick={() => setAdvanced((v) => !v)}
              aria-expanded={advanced}
              className="flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-2.5 text-start text-[12.5px] font-semibold transition hover:bg-muted/50"
            >
              {t("Дополнительные права")}
              <i
                className={cn("ri-arrow-down-s-line text-base leading-none text-muted-foreground transition", advanced && "rotate-180")}
                aria-hidden
              />
            </button>
            {advanced ? (
              <ul className="flex flex-col gap-1 border-t border-border p-2">
                {accessRestrictions.map((r) => {
                  const on = restrictions.includes(r.id);
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => toggle(r.id)}
                        aria-pressed={on}
                        className={cn(
                          "flex w-full cursor-pointer items-start gap-2.5 rounded-btn px-2 py-1.5 text-start transition",
                          on ? "bg-primary/10" : "hover:bg-muted",
                        )}
                      >
                        <i
                          className={cn(
                            "mt-0.5 flex-none text-base leading-none",
                            on ? "ri-checkbox-circle-fill text-primary" : "ri-checkbox-blank-circle-line text-muted-foreground/60",
                          )}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[12px] font-semibold leading-snug">{r.label}</span>
                          <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">{r.text}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-2 border-t border-border bg-background/60 px-5 py-3">
          <div className="ms-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn px-3 text-[12px] font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              {t("Отмена")}
            </button>
            <button
              type="button"
              onClick={submit}
              aria-disabled={!valid || busy}
              className={cn(
                "inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110",
                (!valid || busy) && "cursor-not-allowed opacity-45 hover:brightness-100",
              )}
            >
              {mode === "edit" ? t("Сохранить") : t("Добавить")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserModal;